import React, { useState, useEffect } from 'react';
import { ArrowRight, Zap, Shield, Globe, Smartphone, Building2, BarChart3, Users, ChevronRight, Star, Play, ShoppingCart, Palette, Camera, GraduationCap, Building, CreditCard, TrendingUp, MessageSquare, Moon, Sun } from 'lucide-react';

const InfinitiveLogicLanding = () => {
  const [darkMode, setDarkMode] = useState(false);

  // Initialize theme based on user's system preference
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setDarkMode(true);
    }
  }, []);

  // Toggle dark mode function
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const services = [
    {
      name: "E-commerce & POS",
      category: "Sales Solutions",
      description: "Set up and manage online and in-store sales with an integrated POS system for smooth transactions.",
      features: ["Online Store Setup", "POS Integration", "Inventory Management", "Payment Processing"],
      accent: "blue",
      icon: ShoppingCart,
      popular: true
    },
    {
      name: "Website Development",
      category: "Digital Presence",
      description: "We create modern, mobile-friendly websites and online stores that enhance visibility and drive sales.",
      features: ["Responsive Design", "SEO Optimized", "Mobile-First", "Fast Loading"],
      accent: "emerald",
      icon: Globe,
      popular: true
    },
    
    {
      name: "SACCO Management",
      category: "Financial Systems",
      description: "Enhance financial operations with automated loan processing, member management, and secure transactions.",
      features: ["Loan Processing", "Member Management", "Financial Reporting", "Secure Transactions"],
      accent: "cyan",
      icon: CreditCard,
      popular: true
    },
    {
      name: "HR & Payroll Systems",
      category: "Human Resources",
      description: "Automate HR tasks and payroll processing, ensuring accuracy, compliance, and seamless workforce management.",
      features: ["Payroll Automation", "HR Analytics", "Compliance Management", "Employee Self-Service"],
      accent: "green",
      icon: Users,
      popular: true
    },
    {
      name: "E-Learning Platforms",
      category: "Education Technology",
      description: "Empower education with interactive, scalable, and user-friendly digital learning solutions for all institutions.",
      features: ["Interactive Content", "Student Management", "Progress Tracking", "Mobile Learning"],
      accent: "violet",
      icon: GraduationCap,
      popular: true
    },
    {
      name: "ERP Solutions",
      category: "Enterprise Systems",
      description: "Streamline operations with our tailored ERP systems, enhancing efficiency and business growth.",
      features: ["Custom ERP", "Process Integration", "Real-time Analytics", "Scalable Architecture"],
      accent: "slate",
      icon: Building,
      popular: true
    },
    {
      name: "Business Automation",
      category: "Efficiency Tools",
      description: "Streamline operations with automated workflows, customer relationship management systems.",
      features: ["Workflow Automation", "CRM Integration", "Process Optimization", "Analytics Dashboard"],
      accent: "teal",
      icon: Zap,
      popular: true
    },
    {
      name: "Property Management",
      category: "Real Estate Services",
      description: "Streamline operations, maximize ROI, and ensure tenant satisfaction with our full-service property management solutions.",
      features: ["Tenant Screening", "Rent Collection", "Maintenance Coordination", "Financial Reporting"],
      accent: "teal",
      icon: Building2,
      popular: true
    },
    {
      name: "Training & Support",
      category: "Knowledge Transfer",
      description: "We provide guidance and ongoing support to help businesses leverage our digital tools effectively.",
      features: ["Team Training", "24/7 Support", "Documentation", "Best Practices"],
      accent: "amber",
      icon: Users,
      popular: false
    }
  ];

  const getAccentColors = (accent, isDark) => {
    const colors = {
      blue: { 
        light: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700', gradient: 'from-blue-500 to-blue-600' },
        dark: { bg: 'bg-blue-900/30', border: 'border-blue-700', text: 'text-blue-400', button: 'bg-blue-700 hover:bg-blue-800', gradient: 'from-blue-600 to-blue-800' }
      },
      emerald: { 
        light: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', button: 'bg-emerald-600 hover:bg-emerald-700', gradient: 'from-emerald-500 to-emerald-600' },
        dark: { bg: 'bg-emerald-900/30', border: 'border-emerald-700', text: 'text-emerald-400', button: 'bg-emerald-700 hover:bg-emerald-800', gradient: 'from-emerald-600 to-emerald-800' }
      },
      purple: { 
        light: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700', gradient: 'from-purple-500 to-purple-600' },
        dark: { bg: 'bg-purple-900/30', border: 'border-purple-700', text: 'text-purple-400', button: 'bg-purple-700 hover:bg-purple-800', gradient: 'from-purple-600 to-purple-800' }
      },
      rose: { 
        light: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', button: 'bg-rose-600 hover:bg-rose-700', gradient: 'from-rose-500 to-rose-600' },
        dark: { bg: 'bg-rose-900/30', border: 'border-rose-700', text: 'text-rose-400', button: 'bg-rose-700 hover:bg-rose-800', gradient: 'from-rose-600 to-rose-800' }
      },
      orange: { 
        light: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', button: 'bg-orange-600 hover:bg-orange-700', gradient: 'from-orange-500 to-orange-600' },
        dark: { bg: 'bg-orange-900/30', border: 'border-orange-700', text: 'text-orange-400', button: 'bg-orange-700 hover:bg-orange-800', gradient: 'from-orange-600 to-orange-800' }
      },
      teal: { 
        light: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', button: 'bg-teal-600 hover:bg-teal-700', gradient: 'from-teal-500 to-teal-600' },
        dark: { bg: 'bg-teal-900/30', border: 'border-teal-700', text: 'text-teal-400', button: 'bg-teal-700 hover:bg-teal-800', gradient: 'from-teal-600 to-teal-800' }
      },
      indigo: { 
        light: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-600', button: 'bg-indigo-600 hover:bg-indigo-700', gradient: 'from-indigo-500 to-indigo-600' },
        dark: { bg: 'bg-indigo-900/30', border: 'border-indigo-700', text: 'text-indigo-400', button: 'bg-indigo-700 hover:bg-indigo-800', gradient: 'from-indigo-600 to-indigo-800' }
      },
      amber: { 
        light: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', button: 'bg-amber-600 hover:bg-amber-700', gradient: 'from-amber-500 to-amber-600' },
        dark: { bg: 'bg-amber-900/30', border: 'border-amber-700', text: 'text-amber-400', button: 'bg-amber-700 hover:bg-amber-800', gradient: 'from-amber-600 to-amber-800' }
      },
      slate: { 
        light: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', button: 'bg-slate-600 hover:bg-slate-700', gradient: 'from-slate-500 to-slate-600' },
        dark: { bg: 'bg-slate-700/30', border: 'border-slate-600', text: 'text-slate-300', button: 'bg-slate-700 hover:bg-slate-800', gradient: 'from-slate-600 to-slate-800' }
      },
      green: { 
        light: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', button: 'bg-green-600 hover:bg-green-700', gradient: 'from-green-500 to-green-600' },
        dark: { bg: 'bg-green-900/30', border: 'border-green-700', text: 'text-green-400', button: 'bg-green-700 hover:bg-green-800', gradient: 'from-green-600 to-green-800' }
      },
      cyan: { 
        light: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-600', button: 'bg-cyan-600 hover:bg-cyan-700', gradient: 'from-cyan-500 to-cyan-600' },
        dark: { bg: 'bg-cyan-900/30', border: 'border-cyan-700', text: 'text-cyan-400', button: 'bg-cyan-700 hover:bg-cyan-800', gradient: 'from-cyan-600 to-cyan-800' }
      },
      violet: { 
        light: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', button: 'bg-violet-600 hover:bg-violet-700', gradient: 'from-violet-500 to-violet-600' },
        dark: { bg: 'bg-violet-900/30', border: 'border-violet-700', text: 'text-violet-400', button: 'bg-violet-700 hover:bg-violet-800', gradient: 'from-violet-600 to-violet-800' }
      }
    };
    return isDark ? colors[accent].dark : colors[accent].light;
  };

  const whyChooseUs = [
    {
      title: "Smart Solutions",
      description: "AI-driven innovations and intelligent automation for modern businesses",
      icon: Zap
    },
    {
      title: "Affordable Pricing",
      description: "Cost-effective solutions designed to fit any business budget",
      icon: CreditCard
    },
    {
      title: "Scalable Growth",
      description: "Solutions that grow with your business, from startup to enterprise",
      icon: TrendingUp
    },
    {
      title: "Future-Ready",
      description: "Cutting-edge technology prepared for tomorrow's challenges",
      icon: Star
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 backdrop-blur-md border-b shadow-sm transition-colors duration-300 ${darkMode ? 'bg-gray-900/90 border-gray-800' : 'bg-white/95 border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Infinitive Logic</h1>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Digital Solutions</p>
              </div>
            </div>
            
            <div className="hidden lg:flex items-center space-x-12">
              <a href="#services" className={`hover:text-blue-500 font-medium transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Services</a>
              <a href="#why-us" className={`hover:text-blue-500 font-medium transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Why Us</a>
              <a href="#about" className={`hover:text-blue-500 font-medium transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>About</a>
              <a href="#contact" className={`hover:text-blue-500 font-medium transition-colors ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Contact</a>
            </div>
            
            <div className="flex items-center space-x-4">
              <button 
                onClick={toggleDarkMode} 
                className={`p-3 rounded-full transition-colors ${darkMode ? 'bg-gray-800 text-yellow-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                Get In Touch
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={`pt-32 pb-24 px-6 lg:px-8 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-b from-blue-50 via-white to-white'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-5xl mx-auto">
            <div className={`inline-flex items-center px-6 py-3 border rounded-full mb-8 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-800' : 'bg-gradient-to-r from-green-100 to-purple-100 border-blue-200'}`}>
              <Star className={`w-5 h-5 mr-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
              <span className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>All-in-One Digital Solutions for Modern Businesses</span>
            </div>
            
            <h1 className="text-6xl lg:text-8xl font-bold mb-8 leading-tight">
              <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Sell, Market & </span>
              <br />
              <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-blue-700 bg-clip-text text-transparent">
                Grow Effortlessly
              </span>
            </h1>
            
            <p className={`text-xl lg:text-2xl mb-12 leading-relaxed max-w-4xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Providing <strong>affordable, all-in-one digital solutions</strong> to help businesses thrive. 
             
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
             
              <button className={`border-2 px-12 py-5 rounded-2xl font-bold text-lg transition-all duration-200 flex items-center group ${darkMode ? 'border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'}`}>
                <Play className="mr-3 w-6 h-6" />
                See Our Work
              </button>
            </div>

           
<section 
  id="services" 
  className={`py-8 px-4 transition-colors duration-300 ${
    darkMode ? 'bg-gray-900' : 'bg-gray-50'
  }`}
>
  <div className="max-w-6xl mx-auto">
    {/* Simple Header */}
    <div className="text-center mb-8">
     <h2 className={`text-2xl md:text-4xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
  Select <span className="relative inline-block underline-highlight">An App That Works</span> For You
</h2>

      <p className={`text-sm md:text-base ${
        darkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
      
      </p>
    </div>

   
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {services.map((service, index) => {
        const IconComponent = service.icon;
        const colors = getAccentColors(service.accent, darkMode);
        
        return (
          <div
            key={index}
            className={`group relative bg-white rounded-2xl p-4 transition-all duration-200 hover:shadow-lg cursor-pointer ${
              darkMode 
                ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700' 
                : 'bg-white hover:bg-gray-50 shadow-sm border border-gray-100'
            }`}
          >
            {/* Icon Container - Centered */}
            <div className="flex justify-center mb-3">
              <div className={`w-12 h-12 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center`}>
                <IconComponent className="w-6 h-6 text-white" />
              </div>
            </div>
            
            {/* Service Name - Centered */}
            <h3 className={`text-center text-sm font-medium leading-tight ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {service.name}
            </h3>

            {/* Optional: Small status indicator */}
            
          </div>
        );
      })}
    </div>

    {/* Optional: View All Button 
    <div className="text-center mt-8">
      <button className={`px-6 py-2 rounded-xl text-sm font-medium transition-colors ${
        darkMode 
          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
      }`}>
        View All Services
      </button>
    </div>
    */}
  </div>
</section>
            
            <div className={`rounded-3xl shadow-2xl p-8 max-w-4xl mx-auto transition-colors duration-300 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'}`}>
              <p className={`text-lg mb-6 leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Our Specialties:</strong> E-commerce, Branding, Digital Marketing, ERP Solutions, HR & Payroll Systems, 
                SACCO Management Systems, and E-Learning Platforms—ensuring businesses of all sizes can operate efficiently, 
                reach their audience, and scale with ease.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-500 mb-1">500+</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Projects Delivered</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-500 mb-1">99%</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Client Satisfaction</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-500 mb-1">24/7</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Support Available</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-500 mb-1">AI</div>
                  <div className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Powered Solutions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Why Choose Us Section */}
     <section 
  id="why-us" 
  style={{
    padding: '96px 24px',
    background: darkMode 
      ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' 
      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {/* Animated background elements */}
  <div 
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: darkMode 
        ? 'radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.1) 0%, transparent 50%)'
        : 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.05) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(245, 101, 101, 0.05) 0%, transparent 50%)',
      pointerEvents: 'none'
    }}
  />
  
  <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
    <div style={{ textAlign: 'center', marginBottom: '80px' }}>
      <h2 
        style={{
          fontSize: '3.5rem',
          fontWeight: '800',
          marginBottom: '24px',
          background: darkMode 
            ? 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)'
            : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          lineHeight: '1.1',
          letterSpacing: '-0.02em'
        }}
      >
        Why Choose Infinitive Logic?
      </h2>
      <p 
        style={{
          fontSize: '1.25rem',
          maxWidth: '768px',
          margin: '0 auto',
          color: darkMode ? '#cbd5e1' : '#64748b',
          lineHeight: '1.7',
          fontWeight: '400'
        }}
      >
        Smart, Affordable, Scalable, and Future-Ready Solutions for Every Business
      </p>
    </div>
    
    <div 
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '32px',
        padding: '0 16px'
      }}
    >
      {whyChooseUs.map((item, index) => {
        const IconComponent = item.icon;
        return (
          <div 
            key={index}
            style={{
              background: darkMode 
                ? 'linear-gradient(145deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
                : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              borderRadius: '24px',
              padding: '40px 32px',
              textAlign: 'center',
              border: darkMode 
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: darkMode 
                ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.05), inset 0 1px 0 rgba(148, 163, 184, 0.1)'
                : '0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'translateY(0px)',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              e.currentTarget.style.boxShadow = darkMode 
                ? '0 35px 80px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(148, 163, 184, 0.2), inset 0 1px 0 rgba(148, 163, 184, 0.2)'
                : '0 35px 80px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 1)';
              e.currentTarget.style.borderColor = darkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px) scale(1)';
              e.currentTarget.style.boxShadow = darkMode 
                ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.05), inset 0 1px 0 rgba(148, 163, 184, 0.1)'
                : '0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
              e.currentTarget.style.borderColor = darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(226, 232, 240, 0.8)';
            }}
          >
            {/* Hover effect overlay */}
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: darkMode 
                  ? 'radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
                  : 'radial-gradient(circle at center, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
                borderRadius: '24px',
                opacity: 0,
                transition: 'opacity 0.4s ease',
                pointerEvents: 'none'
              }}
              className="hover-overlay"
            />
            
            <div 
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ef4444 100%)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: darkMode 
                  ? '0 20px 40px -8px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 20px 40px -8px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(10deg) scale(1.1)';
                e.currentTarget.style.boxShadow = darkMode 
                  ? '0 25px 50px -8px rgba(59, 130, 246, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  : '0 25px 50px -8px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
                e.currentTarget.style.boxShadow = darkMode 
                  ? '0 20px 40px -8px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 20px 40px -8px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4)';
              }}
            >
              {/* Animated shimmer effect */}
              <div 
                style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  transform: 'rotate(45deg) translateX(-100%)',
                  animation: 'shimmer 3s ease-in-out infinite'
                }}
              />
              <IconComponent 
                style={{
                  width: '36px',
                  height: '36px',
                  color: '#ffffff',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                }}
              />
            </div>
            
            <h3 
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '16px',
                color: darkMode ? '#f8fafc' : '#1e293b',
                lineHeight: '1.3',
                letterSpacing: '-0.01em'
              }}
            >
              {item.title}
            </h3>
            
            <p 
              style={{
                color: darkMode ? '#94a3b8' : '#64748b',
                lineHeight: '1.7',
                fontSize: '1rem',
                fontWeight: '400',
                margin: 0
              }}
            >
              {item.description}
            </p>
          </div>
        );
      })}
    </div>
  </div>
  
  <style jsx>{`
    @keyframes shimmer {
      0% { transform: rotate(45deg) translateX(-100%); }
      50% { transform: rotate(45deg) translateX(100%); }
      100% { transform: rotate(45deg) translateX(100%); }
    }
    
    @media (max-width: 768px) {
      h2 { fontSize: 2.5rem !important; }
      p { fontSize: 1.125rem !important; }
    }
    
    @media (max-width: 640px) {
      h2 { fontSize: 2rem !important; }
      p { fontSize: 1rem !important; }
    }
  `}</style>
</section>

     

      {/* Mission Section */}
 <section 
  className="py-24 px-6 lg:px-8 relative overflow-hidden" 
  style={{
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #0c1426 70%, #0a0f1c 100%)',
    minHeight: '100vh'
  }}
>
  {/* Premium animated background overlay */}
  <div 
    className="absolute inset-0 opacity-20"
    style={{
      background: `
        radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)
      `
    }}
  />
  
  <div className="max-w-7xl mx-auto relative z-10">
    {/* Section Header */}
    <div className="text-center mb-20">
      <div className="inline-flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)'
          }}
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <span 
          className="text-sm font-semibold tracking-wide uppercase"
          style={{ color: '#60a5fa' }}
        >
          Our Purpose
        </span>
      </div>
      
      <h2 
        className="text-5xl lg:text-7xl font-black mb-6 tracking-tight"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
        }}
      >
        Mission & Vision
      </h2>
      
      <div 
        className="w-24 h-1 mx-auto rounded-full"
        style={{
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #10b981)'
        }}
      />
    </div>

    {/* Mission & Vision Cards Grid */}
    <div className="grid lg:grid-cols-2 gap-8 mb-16">
      {/* Mission Card */}
      <div 
        className="backdrop-blur-xl rounded-3xl p-10 border relative overflow-hidden group hover:scale-105 transition-all duration-500"
        style={{
          background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.7))',
          borderColor: 'rgba(59, 130, 246, 0.3)',
          boxShadow: `
            0 25px 50px -12px rgba(0, 0, 0, 0.8),
            0 0 0 1px rgba(59, 130, 246, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `
        }}
      >
        {/* Card Background Pattern */}
        <div 
          className="absolute top-0 right-0 w-32 h-32 opacity-10 group-hover:opacity-20 transition-opacity duration-500"
          style={{
            background: 'radial-gradient(circle, #3b82f6 2px, transparent 2px)',
            backgroundSize: '20px 20px'
          }}
        />
        
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)'
              }}
            >
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <div>
              <h3 
                className="text-3xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Our Mission
              </h3>
              <div 
                className="w-12 h-0.5 mt-2 rounded-full"
                style={{ background: 'linear-gradient(90deg, #3b82f6, #60a5fa)' }}
              />
            </div>
          </div>
          
          <p className="text-xl leading-relaxed" style={{ color: '#e2e8f0' }}>
            To make digital transformation <span className="font-semibold text-blue-300">simple, affordable, and scalable</span> for every business owner. 
            We streamline operations, improve productivity, and drive business success through <span className="font-semibold text-blue-300">innovation and efficiency</span>.
          </p>
          
          <div className="flex items-center gap-3 mt-8">
            <div className="flex -space-x-2">
              {[1,2,3].map(i => (
                <div key={i} className="w-10 h-10 rounded-full border-2 border-blue-500/30" style={{background: `linear-gradient(135deg, #${i}b82f6, #${i}563eb)`}} />
              ))}
            </div>
            <span className="text-sm text-blue-300 font-medium">Trusted by 500+ businesses</span>
          </div>
        </div>
      </div>

      {/* Vision Card */}
      <div 
        className="backdrop-blur-xl rounded-3xl p-10 border relative overflow-hidden group hover:scale-105 transition-all duration-500"
        style={{
          background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.7))',
          borderColor: 'rgba(139, 92, 246, 0.3)',
          boxShadow: `
            0 25px 50px -12px rgba(0, 0, 0, 0.8),
            0 0 0 1px rgba(139, 92, 246, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `
        }}
      >
        {/* Card Background Pattern */}
        <div 
          className="absolute top-0 right-0 w-32 h-32 opacity-10 group-hover:opacity-20 transition-opacity duration-500"
          style={{
            background: 'radial-gradient(circle, #8b5cf6 2px, transparent 2px)',
            backgroundSize: '20px 20px'
          }}
        />
        
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)'
              }}
            >
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div>
              <h3 
                className="text-3xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Our Vision
              </h3>
              <div 
                className="w-12 h-0.5 mt-2 rounded-full"
                style={{ background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }}
              />
            </div>
          </div>
          
          <p className="text-xl leading-relaxed" style={{ color: '#e2e8f0' }}>
            As we evolve, we are integrating <span className="font-semibold text-purple-300">AI-driven innovations</span> to further enhance 
            business automation and growth, ensuring our clients <span className="font-semibold text-purple-300">stay ahead of the digital curve</span>.
          </p>
          
          <div className="flex items-center gap-3 mt-8">
            <div 
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{
                background: 'rgba(139, 92, 246, 0.2)',
                color: '#a78bfa',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              🚀 AI-Powered Future
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Core Values Section */}
    <div 
      className="backdrop-blur-xl rounded-3xl p-12 border mb-16"
      style={{
        background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.8), rgba(30, 41, 59, 0.6))',
        borderColor: 'rgba(16, 185, 129, 0.3)',
        boxShadow: `
          0 25px 50px -12px rgba(0, 0, 0, 0.8),
          0 0 0 1px rgba(16, 185, 129, 0.1),
          inset 0 1px 0 rgba(255, 255, 255, 0.1)
        `
      }}
    >
      <div className="text-center mb-12">
        <h3 
          className="text-3xl font-bold mb-4"
          style={{
            background: 'linear-gradient(135deg, #10b981, #34d399)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          Our Core Values
        </h3>
        <p className="text-lg" style={{ color: '#cbd5e1' }}>
          The principles that guide everything we do
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {[
          { icon: "⚡", title: "Innovation", desc: "Cutting-edge solutions for modern challenges" },
          { icon: "🤝", title: "Partnership", desc: "Your success is our success" },
          { icon: "🎯", title: "Excellence", desc: "Delivering beyond expectations, every time" }
        ].map((value, i) => (
          <div key={i} className="text-center group">
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(52, 211, 153, 0.1))',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}
            >
              {value.icon}
            </div>
            <h4 className="text-xl font-bold mb-3 text-emerald-300">{value.title}</h4>
            <p style={{ color: '#cbd5e1' }}>{value.desc}</p>
          </div>
        ))}
      </div>
    </div>

    
  </div>
</section>

      {/* CTA Section */}
      <section id="contact" className={`py-24 px-6 lg:px-8 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className={`text-5xl lg:text-6xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Ready to Transform Your Business?
          </h2>
          <p className={`text-xl mb-12 max-w-2xl mx-auto leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Let's discuss how our comprehensive digital solutions can help your business sell more, 
            market better, and grow faster. Get in touch with our team today.
          </p>
          
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-12 py-5 rounded-2xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-2xl flex items-center justify-center group">
              Get In Touch
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-12 py-5 rounded-2xl font-bold text-lg hover:border-gray-400 hover:bg-gray-100 transition-all duration-200">
              View Our Portfolio
            </button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl"></div>
              <div className="font-semibold text-gray-900">Email Us</div>
              <div className="text-gray-600">hello@infinitivelogic.com</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl"></div>
              <div className="font-semibold text-gray-900">Call Us</div>
              <div className="text-gray-600">+254 (0) 700 000 000</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl"></div>
              <div className="font-semibold text-gray-900">Visit Us</div>
              <div className="text-gray-600">Nairobi, Kenya</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Infinitive Logic</h3>
                  <p className="text-gray-400 text-sm">Digital Solutions</p>
                </div>
              </div>
              <p className="text-gray-400 max-w-md leading-relaxed mb-6">
                Empowering businesses with affordable, scalable digital solutions. 
                From E-commerce to AI-driven automation, we make digital transformation simple and effective.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-gray-400">📘</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-gray-400">📱</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-gray-400">🐦</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Our Services</h4>
              <div className="space-y-3">
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">E-commerce & POS</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Website Development</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">HR & Payroll</a>

                <a href="#" className="block text-gray-400 hover:text-white transition-colors">ERP solutions</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Sacco Management System</a>

                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Property Management</a>

                <a href="#" className="block text-gray-400 hover:text-white transition-colors">School Finance System</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Training & Support</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <div className="space-y-3">
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">About Us</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Our Portfolio</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Careers</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Contact</a>
                <a href="#" className="block text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 Infinitive Logic. All rights reserved. | Making digital transformation simple, affordable, and scalable.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InfinitiveLogicLanding;